import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionContext';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Box,
  Alert,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Delete, Visibility, Add, Refresh, LinkOff, Map as MapIcon } from '@mui/icons-material';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import * as trackerApi from '../services/trackerApi';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const TrackerDashboard = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [trackers, setTrackers] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [commandStats, setCommandStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openMapDialog, setOpenMapDialog] = useState(false);
  const [selectedTrackerLocation, setSelectedTrackerLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);

  // Permission checks
  const canEditTrackers = hasPermission('tracker', 'edit');
  const canDeleteTrackers = hasPermission('tracker', 'delete');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    sim_number: '',
    customId: ''
  });
  const [selectedBookmark, setSelectedBookmark] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [trackersData, statsData, bookmarksData] = await Promise.all([
        trackerApi.getAllTrackers(),
        trackerApi.getCommandStats(),
        trackerApi.getAllBookmarks()
      ]);

      setTrackers(trackersData.trackers || []);
      setCommandStats(statsData);

      // Handle different response formats for bookmarks
      // Could be: { bookmarks: [...] } or just [...]
      if (Array.isArray(bookmarksData)) {
        setBookmarks(bookmarksData);
      } else if (bookmarksData.bookmarks && Array.isArray(bookmarksData.bookmarks)) {
        setBookmarks(bookmarksData.bookmarks);
      } else {
        setBookmarks([]);
      }

      setError('');
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setFormData({ name: '', sim_number: '', customId: '' });
    setSelectedBookmark(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ name: '', sim_number: '', customId: '' });
    setSelectedBookmark(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBookmarkChange = (event, newValue) => {
    setSelectedBookmark(newValue);
    setFormData({
      ...formData,
      customId: newValue ? newValue.customId : ''
    });
  };

  const handleRegisterTracker = async () => {
    try {
      if (!formData.name || !formData.sim_number) {
        setError('Name and SIM number are required');
        return;
      }

      await trackerApi.registerTracker(formData);
      setSuccess('Tracker registered successfully');
      setError('');
      handleCloseDialog();
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to register tracker');
    }
  };

  const handleUnlinkTracker = async (trackerId) => {
    if (!window.confirm('Are you sure you want to unlink this tracker from its bookmark?')) {
      return;
    }

    try {
      await trackerApi.updateTracker(trackerId, { customId: '' });
      setSuccess('Tracker unlinked successfully');
      setError('');
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to unlink tracker');
    }
  };

  const handleDeleteTracker = async (trackerId) => {
    if (!window.confirm('Are you sure you want to delete this tracker? This action cannot be undone.')) {
      return;
    }

    try {
      await trackerApi.deleteTracker(trackerId);
      setSuccess('Tracker deleted successfully');
      setError('');
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to delete tracker');
    }
  };

  const handleViewTracker = (trackerId) => {
    navigate(`/dashboard/trackers/${trackerId}`);
  };

  const handleViewMap = async (tracker) => {
    try {
      setLoadingLocation(true);
      const logsData = await trackerApi.getTrackerLogs(tracker.id);

      // Find the most recent log with coordinates
      const logWithCoords = logsData.logs?.find(log => log.latitude && log.longitude);

      if (logWithCoords) {
        setSelectedTrackerLocation({
          tracker: tracker,
          position: {
            lat: parseFloat(logWithCoords.latitude),
            lng: parseFloat(logWithCoords.longitude)
          },
          timestamp: logWithCoords.updatedAt,
          speed: logWithCoords.response?.match(/Speed:(\d+)km\/h/)?.[1] || 'N/A',
          course: logWithCoords.response?.match(/Course:(\d+)/)?.[1] || 'N/A'
        });
        setOpenMapDialog(true);
      } else {
        setError('No location data available for this tracker');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch tracker location');
      console.error('Error fetching tracker location:', err);
    } finally {
      setLoadingLocation(false);
    }
  };

  const handleCloseMapDialog = () => {
    setOpenMapDialog(false);
    setSelectedTrackerLocation(null);
  };

  // Filter trackers based on search query
  const filteredTrackers = trackers.filter((tracker) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      tracker.name.toLowerCase().includes(query) ||
      tracker.sim_number.toLowerCase().includes(query) ||
      (tracker.customId && tracker.customId.toLowerCase().includes(query)) ||
      (tracker.bookmark?.vlicensePlate && tracker.bookmark.vlicensePlate.toLowerCase().includes(query)) ||
      (tracker.bookmark?.vvinNo && tracker.bookmark.vvinNo.toLowerCase().includes(query))
    );
  });

  // Prepare chart data
  const barChartData = commandStats ? {
    labels: Object.keys(commandStats.commandsPerTracker || {}),
    datasets: [
      {
        label: 'Commands Sent',
        data: Object.values(commandStats.commandsPerTracker || {}),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }
    ]
  } : null;

  const lineChartData = commandStats ? {
    labels: Object.keys(commandStats.commandsPerDay || {}),
    datasets: [
      {
        label: 'Commands Per Day',
        data: Object.values(commandStats.commandsPerDay || {}),
        fill: false,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        tension: 0.1
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      }
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        MT05S GPS Tracker Management
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Action Buttons and Search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
          onClick={handleOpenDialog}
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          Register New Tracker
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Refresh sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
          onClick={fetchData}
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          Refresh
        </Button>
        <TextField
          placeholder="Search trackers..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 300 },
            ml: { sm: 'auto' },
            '& .MuiInputBase-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' }
            },
            '& .MuiFormHelperText-root': {
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }
          }}
          helperText={`Showing ${filteredTrackers.length} of ${trackers.length} trackers`}
        />
      </Box>

      {/* Charts Section */}
      {commandStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Commands Per Tracker
              </Typography>
              {barChartData && <Bar data={barChartData} options={chartOptions} />}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Command Activity (Last 7 Days)
              </Typography>
              {lineChartData && <Line data={lineChartData} options={chartOptions} />}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Trackers Table */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Registered Trackers ({filteredTrackers.length})
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>SIM Number</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Linked Bookmark</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>License Plate</TableCell>
                {canEditTrackers && <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Owner</TableCell>}
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Registered</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTrackers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEditTrackers ? 7 : 6} align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {searchQuery ? 'No trackers match your search' : 'No trackers registered yet'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTrackers.map((tracker) => (
                  <TableRow key={tracker.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{tracker.name}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{tracker.sim_number}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{tracker.customId || 'None'}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {tracker.bookmark?.vlicensePlate || '-'}
                    </TableCell>
                    {canEditTrackers && (
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{tracker.user?.username || '-'}</TableCell>
                    )}
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {new Date(tracker.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="success"
                        onClick={() => handleViewMap(tracker)}
                        title="View on Map"
                        disabled={loadingLocation}
                      >
                        <MapIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => handleViewTracker(tracker.id)}
                        title="View Details"
                      >
                        <Visibility sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                      {tracker.customId && (
                        <IconButton
                          color="warning"
                          onClick={() => handleUnlinkTracker(tracker.id)}
                          title="Unlink from Bookmark"
                        >
                          <LinkOff sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        </IconButton>
                      )}
                      {canDeleteTrackers && (
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteTracker(tracker.id)}
                          title="Delete Tracker"
                        >
                          <Delete sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Register Tracker Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Register New GPS Tracker</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Tracker Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              placeholder="e.g., My Motorcycle Tracker"
            />
            <TextField
              label="SIM Number"
              name="sim_number"
              value={formData.sim_number}
              onChange={handleInputChange}
              fullWidth
              required
              placeholder="+1234567890"
              helperText="Include country code (e.g., +1 for US)"
            />
            <Autocomplete
              fullWidth
              options={bookmarks}
              value={selectedBookmark}
              onChange={handleBookmarkChange}
              getOptionLabel={(option) =>
                `${option.customId} - ${option.vlicensePlate || option.vvinNo || option.vbusinessName || 'No identifier'}`
              }
              isOptionEqualToValue={(option, value) => option.customId === value.customId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Link to Bookmark (Optional)"
                  placeholder="Search by customID, license plate, or VIN"
                  helperText="Start typing to search bookmarks"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.customId}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {option.customId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.vlicensePlate && `License: ${option.vlicensePlate}`}
                      {option.vvinNo && ` | VIN: ${option.vvinNo}`}
                      {option.vbusinessName && ` | ${option.vbusinessName}`}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText="No bookmarks found"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleRegisterTracker} variant="contained" color="primary">
            Register
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map View Dialog */}
      <Dialog open={openMapDialog} onClose={handleCloseMapDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Tracker Location: {selectedTrackerLocation?.tracker?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTrackerLocation && (
            <Box sx={{ mt: 2 }}>
              <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '400px' }}
                  center={selectedTrackerLocation.position}
                  zoom={15}
                >
                  <Marker
                    position={selectedTrackerLocation.position}
                    title={selectedTrackerLocation.tracker.name}
                  />
                </GoogleMap>
              </LoadScript>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Latitude
                    </Typography>
                    <Typography variant="body1">
                      {selectedTrackerLocation.position.lat.toFixed(6)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Longitude
                    </Typography>
                    <Typography variant="body1">
                      {selectedTrackerLocation.position.lng.toFixed(6)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Speed
                    </Typography>
                    <Typography variant="body1">
                      {selectedTrackerLocation.speed} km/h
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Course
                    </Typography>
                    <Typography variant="body1">
                      {selectedTrackerLocation.course}°
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedTrackerLocation.timestamp).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMapDialog}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleCloseMapDialog();
              handleViewTracker(selectedTrackerLocation?.tracker?.id);
            }}
          >
            View Full Details
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrackerDashboard;
